/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  GenerateReportSchema,
  REPORT_TYPES,
  type GenerateReportInput,
} from '@therapy/shared-validators';
import { FileBarChart, Loader2 } from 'lucide-react';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { type FieldErrors, type UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type GenerateReportPayload } from '../../lib/api/reports.js';
import { type Client } from '../../lib/api/resources.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useGenerateReport } from '../../lib/queries/reports.js';

const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** Report types that collect an explicit `YYYY-MM-DD` date range. */
function isRangeReport(type: GenerateReportInput['reportType']): boolean {
  return type === 'attendance' || type === 'revenue';
}

/** Build the request payload, spreading only the fields each report type uses. */
function toPayload(values: GenerateReportInput): GenerateReportPayload {
  if (values.reportType === 'client_progress') {
    return {
      reportType: values.reportType,
      ...(values.clientId !== undefined ? { clientId: values.clientId } : {}),
    };
  }
  if (isRangeReport(values.reportType)) {
    return {
      reportType: values.reportType,
      ...(values.dateFrom !== undefined ? { dateFrom: values.dateFrom } : {}),
      ...(values.dateTo !== undefined ? { dateTo: values.dateTo } : {}),
    };
  }
  // Period + aggregate reports derive their own scope/range on the server.
  return { reportType: values.reportType };
}

interface ScopeFieldsProps {
  readonly reportType: GenerateReportInput['reportType'];
  readonly register: UseFormRegister<GenerateReportInput>;
  readonly errors: FieldErrors<GenerateReportInput>;
  readonly clients: readonly Client[];
}

/** The scope inputs a report type needs: a client picker, a date range, or none. */
function ScopeFields({
  reportType,
  register,
  errors,
  clients,
}: ScopeFieldsProps): JSX.Element | null {
  const { t } = useTranslation();
  if (reportType === 'client_progress') {
    return (
      <div>
        <label htmlFor="report-client" className={LABEL_CLASS}>
          {t('reports.field.client')}
        </label>
        <select
          id="report-client"
          className={SELECT_CLASS}
          defaultValue=""
          aria-invalid={errors.clientId !== undefined}
          {...register('clientId')}
        >
          <option value="" disabled>
            {t('reports.field.selectClient')}
          </option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.fullName}
            </option>
          ))}
        </select>
        {errors.clientId !== undefined && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {errors.clientId.message}
          </p>
        )}
      </div>
    );
  }
  if (isRangeReport(reportType)) {
    return (
      <div className="grid grid-cols-2 gap-3">
        <FormField
          id="report-from"
          type="date"
          label={t('reports.field.from')}
          autoComplete="off"
          error={errors.dateFrom?.message}
          registration={register('dateFrom')}
        />
        <FormField
          id="report-to"
          type="date"
          label={t('reports.field.to')}
          autoComplete="off"
          error={errors.dateTo?.message}
          registration={register('dateTo')}
        />
      </div>
    );
  }
  return null;
}

/**
 * "Generate a report" panel (Figma Reports). Report type drives which inputs
 * show — a client picker for Client Progress, a date range for Attendance /
 * Revenue, nothing for the period + aggregate reports. Validated by the shared
 * `GenerateReportSchema`; the new job appears in the list below and updates live.
 */
export function ReportForm(): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const generate = useGenerateReport();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<GenerateReportInput>({
    resolver: zodResolver(GenerateReportSchema),
    defaultValues: { reportType: 'client_progress' },
  });
  const reportType = watch('reportType');

  const submit = useCallback(
    async (values: GenerateReportInput): Promise<void> => {
      await generate.mutateAsync(toPayload(values));
      reset({ reportType: values.reportType });
    },
    [generate, reset],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form
      onSubmit={onFormSubmit}
      className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800"
      noValidate
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="report-type" className={LABEL_CLASS}>
            {t('reports.field.type')}
          </label>
          <select id="report-type" className={SELECT_CLASS} {...register('reportType')}>
            {REPORT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`reports.type.${type}`)}
              </option>
            ))}
          </select>
        </div>

        <ScopeFields
          reportType={reportType}
          register={register}
          errors={errors}
          clients={clients.data ?? []}
        />
      </div>

      {generate.isError && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {t('reports.generateError')}
        </p>
      )}

      <div className="mt-5 flex justify-end">
        <Button type="submit" disabled={isSubmitting || generate.isPending}>
          {isSubmitting || generate.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <FileBarChart className="h-4 w-4" aria-hidden="true" />
          )}
          {t('reports.generate')}
        </Button>
      </div>
    </form>
  );
}
